import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame, Zap, Waves, Check } from "lucide-react";
import Ornament, { SectionOverline } from "../components/Ornament";
import { api } from "../lib/config";
import useSEO from "../hooks/useSEO";

const MACHINES = [
  {
    icon: Flame,
    name: "Laser CO₂",
    tagline: "Matières organiques & transparentes",
    desc: "Gravure et découpe sur bois, acrylique, verre, cuir, tissu, papier. Rendu précis et esthétique pour vos pièces les plus délicates.",
    image: "https://customer-assets.emergentagent.com/job_verre-gravure-demo/artifacts/y1jcsmng_AKM%20Laser%20machine%20%C3%A0%20vendre%20pas%20ch%C3%A8re%20d%27occasion%20dans%20le%20sud%20de%20la%20france%20Lyon.png",
  },
  {
    icon: Zap,
    name: "Laser Fibre",
    tagline: "Métaux & matières dures",
    desc: "Gravure sur inox, aluminium, laiton, or, argent et matières dures. Rendu contrasté et durable pour plaques, bijoux, trophées.",
    image: "https://customer-assets.emergentagent.com/job_verre-gravure-demo/artifacts/vde5m8kr_AKM%20Laser%20machine%20gravure%20et%20d%C3%A9coupe%20m%C3%A9tal%20petite%20Lyon%20sud%20de%20france2.jpg",
  },
  {
    icon: Waves,
    name: "Sablage",
    tagline: "Verre & matériaux haut de gamme",
    desc: "Technique de gravure par projection de sable sur verre et autres matériaux. Rendu mat et profond, pour un rendu plus haut de gamme, idéal pour les objets fragiles ou imposants comme les carafes, bouteilles (vin, champagne, whisky, rhum, etc.), vases, etc.",
    image: "https://customer-assets.emergentagent.com/job_verre-gravure-demo/artifacts/ypts6kxu_image.png",
  },
];

const MATERIALS = [
  "Verre", "Cristal", "Inox", "Aluminium", "Laiton", "Or", "Argent",
  "Bois massif", "Contreplaqué", "Bambou", "Liège", "Acrylique / Plexiglas",
  "Cuir", "Tissu", "Céramique", "Ardoise", "Pierre", "Inox peint", "Papier & carton",
];

const OBJECTS = [
  "Verres à vin", "Flûtes à champagne", "Tasses & mugs", "Carafes", "Vases",
  "Planches à découper", "Couteaux", "Bouteilles", "Coffrets & boîtes",
  "Porte-clés", "Plaques", "Trophées & médailles", "Bijoux", "Cadres",
  "Miroirs", "Téléphones & tablettes", "Stylos", "Montres",
];

const PROCESS = [
  { step: "01", title: "Premier contact", text: "Vous avez un projet ? Envoyez-nous un message en décrivant votre idée. Nous vous proposons un rendez-vous pour définir ensemble la matière, les couleurs, la police, la quantité et les délais." },
  { step: "02", title: "Devis et maquette", text: "Nous vous transmettons un devis personnalisé, accompagné d'une maquette digitale si besoin pour visualiser le résultat. Un prototype peut être réalisé si nécessaire en supplément." },
  { step: "03", title: "Production", text: "Toutes nos réalisations sont fabriquées dans notre atelier. Nous disposons d'un stock de matières premières pour une production rapide. Taille maximale des supports : 1300 × 600 cm." },
  { step: "04", title: "Tarifs", text: "Les tarifs s'adaptent à la quantité. Pour toute commande supérieure à 150 €, un acompte de 40 % est demandé à la validation. Le solde est réglé à la livraison." },
  { step: "05", title: "Livraison", text: "Votre commande peut être récupérée en atelier ou expédiée à l'adresse de votre choix : point relais, locker ou domicile. Tous nos envois sont soigneusement protégés." },
];

const AtelierPage = () => {
  useSEO({
    title: "L'Atelier",
    description: "Découvrez nos techniques : laser CO₂ pour bois, verre et acrylique, laser fibre pour métaux (inox, aluminium, laiton, or, argent), sablage haut de gamme pour verre et cristal. Processus, matériaux, savoir-faire artisanal.",
  });
  const [story, setStory] = useState(null);
  const [gallery, setGallery] = useState([]);
  useEffect(() => {
    (async () => {
      const [c, g] = await Promise.all([api.get("/content"), api.get("/gallery")]);
      setStory(c.data.atelier_story);
      setGallery(g.data.slice(0, 6));
    })();
  }, []);

  return (
    <div data-testid="atelier-page">
      {/* HERO */}
      <section className="pt-32 pb-20 bg-[#171717]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Ornament className="mb-6" />
          <SectionOverline>L'Atelier</SectionOverline>
          <h1 className="font-serif text-5xl sm:text-6xl text-white mt-4 mb-6">Un savoir-faire, une passion</h1>
          <p className="text-lg text-[#DDD4B2]/80 max-w-2xl mx-auto leading-relaxed">
            Depuis plus de 15 ans, nous perpétuons un artisanat exigeant au service de vos créations les plus précieuses.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section className="py-24 bg-[#DDD4B2] text-[#171717]">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <motion.img
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            src={story?.image || "https://images.unsplash.com/photo-1752051548874-7d2176a84007"}
            alt="Artisan"
            className="w-full h-[500px] object-cover"
          />
          <div>
            <SectionOverline>Notre histoire</SectionOverline>
            <h2 className="font-serif text-4xl sm:text-5xl mt-3 mb-6 leading-tight">{story?.title || "L'histoire de Passion Verre"}</h2>
            <p className="text-base leading-relaxed text-[#171717]/85 whitespace-pre-line" data-testid="atelier-story-text">
              {story?.text}
            </p>
          </div>
        </div>
      </section>

      {/* MACHINES */}
      <section className="py-24 sm:py-32 bg-[#171717]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <SectionOverline>Nos techniques</SectionOverline>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#DDD4B2] mt-3">Trois machines, un infini de possibles</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {MACHINES.map((m, i) => (
              <motion.div
                key={m.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className="bg-[#0f0f0f] border border-[#9A8640]/30 hover:border-[#9A8640]/70 transition-all overflow-hidden"
                data-testid={`machine-card-${i}`}
              >
                <div className="h-64 overflow-hidden">
                  <img src={m.image} alt={m.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-8">
                  <div className="inline-flex items-center justify-center w-11 h-11 border border-[#9A8640] text-[#9A8640] mb-5">
                    <m.icon size={20} />
                  </div>
                  <h3 className="font-serif text-2xl text-white mb-1">{m.name}</h3>
                  <p className="text-xs uppercase tracking-[0.18em] text-[#9A8640] mb-4">{m.tagline}</p>
                  <p className="text-sm text-[#DDD4B2]/80 leading-relaxed">{m.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MATERIALS + OBJECTS */}
      <section className="py-24 bg-[#DDD4B2] text-[#171717]">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16">
          <div>
            <SectionOverline>Matériaux</SectionOverline>
            <h2 className="font-serif text-3xl sm:text-4xl mt-3 mb-6">Ce que nous gravons</h2>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              {MATERIALS.map((m) => (
                <div key={m} className="flex items-center gap-2 text-sm">
                  <Check size={14} className="text-[#9A8640] shrink-0" />
                  <span>{m}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <SectionOverline>Objets</SectionOverline>
            <h2 className="font-serif text-3xl sm:text-4xl mt-3 mb-6">Ce que nous personnalisons</h2>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              {OBJECTS.map((o) => (
                <div key={o} className="flex items-center gap-2 text-sm">
                  <Check size={14} className="text-[#9A8640] shrink-0" />
                  <span>{o}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 sm:py-32 bg-[#171717]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <SectionOverline>Notre processus</SectionOverline>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#DDD4B2] mt-3">De l'idée à la livraison</h2>
          </div>
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-px bg-[#9A8640]/30 md:left-1/2" />
            <div className="space-y-12">
              {PROCESS.map((p, i) => (
                <motion.div
                  key={p.step}
                  initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className={`relative pl-20 md:pl-0 md:grid md:grid-cols-2 md:gap-12 ${i % 2 === 0 ? "md:text-right" : "md:flex-row-reverse"}`}
                  data-testid={`process-step-${p.step}`}
                >
                  <div className={i % 2 === 0 ? "md:pr-16" : "md:col-start-2 md:pl-16"}>
                    <span className="font-serif text-5xl text-[#9A8640]/70">{p.step}</span>
                    <h3 className="font-serif text-2xl text-white mt-2 mb-3">{p.title}</h3>
                    <p className="text-sm text-[#DDD4B2]/75 leading-relaxed">{p.text}</p>
                  </div>
                  <div className="absolute left-0 md:left-1/2 top-0 md:-translate-x-1/2 w-16 h-16 rounded-full bg-[#171717] border border-[#9A8640] flex items-center justify-center text-[#9A8640] font-serif text-xl">
                    {p.step}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          {/* CHARTE GRAPHIQUE NOTE */}
          <div className="mt-20 max-w-3xl mx-auto px-6">
            <div className="border border-[#9A8640]/30 p-8 sm:p-10 bg-[#0f0f0f]">
              <p className="text-xs uppercase tracking-[0.2em] text-[#9A8640] mb-3">Charte graphique</p>
              <p className="text-base leading-relaxed text-[#DDD4B2]/85">
                Vous avez un logo ou une illustration ? Nous acceptons les formats <span className="text-[#9A8640] font-medium">.png</span>, <span className="text-[#9A8640] font-medium">.jpeg</span> et <span className="text-[#9A8640] font-medium">.dxf</span>. Vous avez une police personnalisée ? Transmettez-nous le fichier <span className="text-[#9A8640] font-medium">.ttf</span> et nous l'intégrons à votre projet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      {gallery.length > 0 && (
        <section className="py-24 bg-[#0f0f0f]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <SectionOverline>Nos réalisations</SectionOverline>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#DDD4B2] mt-3">Un aperçu</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {gallery.map((g) => (
                <div key={g.id} className="aspect-square overflow-hidden group">
                  <img src={g.image_url} alt={g.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default AtelierPage;
